/**
 * Device Detection Utilities
 * 
 * Functions to detect device types and capabilities
 */

/**
 * Check if the current device is an actual mobile device (not just a small browser window)
 * Uses user agent detection to distinguish real mobile devices from desktop browsers
 * 
 * @returns true if it's an actual mobile device, false if it's a desktop browser (even with small window)
 */
export const isActualMobileDevice = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  
  // Check for mobile device patterns in user agent
  const mobilePatterns = [
    /android/i,
    /webos/i,
    /iphone/i,
    /ipad/i,
    /ipod/i,
    /blackberry/i,
    /windows phone/i,
    /mobile/i,
  ];
  
  // Check if user agent matches mobile patterns
  const isMobileUserAgent = mobilePatterns.some(pattern => pattern.test(userAgent));
  
  // Additional check: if it's iPad, it might be detected as mobile but we want to allow it
  // iPadOS 13+ reports as macOS, so we need to check for touch capability
  const isIPad = /ipad/i.test(userAgent) || 
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  
  // Return true only if it's a mobile user agent (but allow iPad to be treated as tablet/desktop)
  // For the purpose of blocking in web-only mode, we want to block phones but allow tablets/iPads
  // Actually, let's be more specific: block phones and small tablets, but allow larger tablets
  if (isIPad) {
    // iPad is large enough to use desktop mode, so don't block it
    return false;
  }
  
  return isMobileUserAgent;
};

/**
 * Check if the current device has touch capability
 * Useful for detecting touch-enabled devices like tablets
 */
export const hasTouchCapability = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );
};
